// Read-only detail view for a meeting. Renders through the shared
// TrackingDetailPanel: the recurring-schedule chip + per-status primary
// action (Mark held / Add minutes link) compose into the panel's badges
// and actions slots, the meeting fields into its <dl>, and the evidence
// links + notes sections into its children slot. The legacy `frequency`
// free-text and `minutesLink` URL are preserved here as read-only
// captions / link rows so historical records keep their context until
// they're edited through the new form (which migrates minutesLink into
// evidenceLinks).

import { Check, ExternalLink, Paperclip, Pencil } from "lucide-react";
import { safeExternalHref } from "../../../../utils/evidenceLinks";
import Button from "../../../common/Button";
import TrackingDetailPanel, {
  type TrackingDetailField,
} from "../shared/TrackingDetailPanel";
import type { Meeting } from "../../../../types";

interface MeetingDetailExpandProps {
  item: Meeting;
  onEdit?: () => void;
  /** Optional — present when the row's status accepts a Mark-held
   *  transition. Hidden when omitted (no orphaned UI). */
  onMarkHeld?: () => void;
  /** Optional — present when the row is held without minutes. */
  onAddMinutes?: () => void;
  /** Lookup-resolved meeting type label. Falls back to the raw
   *  `meetingType` value (the lookup itemId) when not supplied. */
  meetingTypeLabel?: string;
}

const FREQUENCY_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  annually: "Annually",
};

function fmtDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU");
  } catch {
    return v;
  }
}

function describeRecurrence(rule: Meeting["recurrenceRule"]): string | null {
  if (!rule) return null;
  const base = FREQUENCY_LABELS[rule.frequency] ?? rule.frequency;
  const interval =
    rule.interval && rule.interval > 1 ? ` × ${rule.interval}` : "";
  const range = `from ${fmtDate(rule.startDate)}${rule.endDate ? ` until ${fmtDate(rule.endDate)}` : ""}`;
  return `${base}${interval}, ${range}`;
}

export default function MeetingDetailExpand({
  item,
  onEdit,
  onMarkHeld,
  onAddMinutes,
  meetingTypeLabel,
}: MeetingDetailExpandProps) {
  const isRecurringParent = !!item.recurrenceRule;
  const recurrence = describeRecurrence(item.recurrenceRule);
  const hasEvidence = (item.evidenceLinks ?? []).length > 0;

  // Primary action — driven by stored status + evidence presence. The
  // callbacks are optional so the parent can short-circuit the row
  // (e.g. archived rows pass neither and the panel renders without
  // any primary button).
  const showMarkHeld =
    !!onMarkHeld && item.status !== "held" && item.status !== "cancelled";
  const showAddMinutes =
    !!onAddMinutes && item.status === "held" && !hasEvidence;

  const fields: TrackingDetailField[] = [
    { label: "Meeting type", value: meetingTypeLabel ?? item.meetingType },
  ];
  if (item.contractReference) {
    fields.push({
      label: "Contract reference",
      value: item.contractReference,
    });
  }
  if (!isRecurringParent) {
    fields.push({
      label: "Date",
      value: (
        <>
          <span>{fmtDate(item.date)}</span>
          {item.frequency && (
            <span className="ml-2 text-caption text-slate-500 dark:text-slate-400">
              · {item.frequency}
            </span>
          )}
        </>
      ),
    });
  }
  if (recurrence) {
    fields.push({ label: "Recurrence", value: recurrence });
  }
  if (!isRecurringParent && item.attendees && item.attendees.length > 0) {
    fields.push({ label: "Attendees", value: item.attendees.join(", ") });
  }
  if (!isRecurringParent && item.minutesLink) {
    fields.push({
      label: "Minutes",
      value: (
        <a
          href={item.minutesLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-500 dark:text-brand-400 hover:underline"
        >
          View minutes
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ),
    });
  }

  return (
    <TrackingDetailPanel
      testId="meeting-detail-expand"
      title={item.name}
      badges={
        isRecurringParent ? (
          <span
            className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-caption font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300"
            title="This is the recurring schedule, not a single occurrence."
          >
            Recurring schedule
          </span>
        ) : undefined
      }
      description={item.description}
      fields={fields}
      actions={
        <>
          {showMarkHeld && (
            <Button
              variant="primary"
              size="sm"
              icon={<Check className="h-4 w-4" />}
              onClick={onMarkHeld}
            >
              Mark held
            </Button>
          )}
          {showAddMinutes && (
            <Button
              variant="primary"
              size="sm"
              icon={<Paperclip className="h-4 w-4" />}
              onClick={onAddMinutes}
            >
              Add minutes link
            </Button>
          )}
          {onEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" />}
              onClick={onEdit}
            >
              Edit
            </Button>
          )}
        </>
      }
    >
      {!isRecurringParent &&
        item.evidenceLinks &&
        item.evidenceLinks.length > 0 && (
          <div>
            <h4 className="text-caption text-slate-500 dark:text-slate-400">
              Evidence links
            </h4>
            <ul className="mt-2 space-y-1">
              {item.evidenceLinks.map((link, i) => {
                const href = safeExternalHref(link.url);
                const text = link.label || link.url;
                return (
                  <li key={i}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-body text-brand-600 hover:text-brand-500 dark:text-brand-400 hover:underline"
                      >
                        {text}
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <span className="text-body text-slate-700 dark:text-slate-300">
                        {text}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

      {item.notes && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Notes
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.notes}
          </p>
        </div>
      )}
    </TrackingDetailPanel>
  );
}
